// Soft particles (art/model-integration.md 9.5 (2)): the bursts, the fires
// and the smoke that used to be the world's block particles, drawn as cards
// cut from the flipbooks baked in Blender (art/fx, 1g).
//
// A burst was a heap of instanced cubes: an explosion 38 orange and black
// crates flung outward, a broadside's muzzle blast a hundred and seventy of
// them, a splash a spray of blue bricks. Next to the tracers that burn and
// the smoke ropes that hang (streaks.js) they read as lumber. Here a burst is
// a handful of cards, each playing a simulation's frames: the white flash,
// the fireball cooling through orange, the black smoke climbing off it; the
// water column climbing and falling back as spray; the puff of an 88 hanging
// in the sky.
//
// How a card is drawn:
//   * It faces the eye and stands UPRIGHT in the world (the clouds' rule,
//     clouds.js): a chase camera rolled into a turn must not tip a splash
//     column over. Seen from straight above, where upright means nothing, it
//     eases onto the camera's own axes. A card may instead lie along an axis
//     (a muzzle flash along its bore), stand on an axis of its own (a flame
//     streaming back off a burning airframe stands on the wind, not the
//     sky), or be rolled a little for variety.
//   * It draws only its frame's trimmed rectangle (scripts/fx-atlas.mjs): a
//     fresh flash covers a tenth of its frame, a splash column a third of its
//     width, and every empty texel inside a card is fill a phone pays for.
//   * It is slid toward the eye by a fraction of its size, and scaled back up
//     so it covers the same pixels: the card's plane then stands in front of
//     the ground the burst sits on, and a fireball on a field no longer cuts
//     a hard line where its lower half meets the dirt. (A true depth fade
//     needs the scene's depth, which tier 1 never renders to a texture.)
//   * It thins away as the eye reaches it, and collapses before it is
//     rasterized once it's gone, so flying through a burst doesn't paste a
//     sheet on the lens or fill the screen with fragments that all discard.
//   * Premultiplied (ONE, ONE_MINUS_SRC_ALPHA), whatever the sheet: straight
//     gas occludes, a fire's light is added where its coverage is nil, and a
//     fireball does both. One blend means one draw — every card on the map,
//     sorted back to front, so smoke lies over a splash lies over a fireball
//     as they actually stand.
//   * Gas is lit by the sky's own sun and fill, as the smoke ropes are: white
//     at noon, gold at dusk, slate at night. Fire is light: tier 2's emitter
//     hook lifts it into HDR so the bloom finds it (gfx2.js).
//
// Nothing here knows what a bomb or a salvo is; effects.js says what to
// draw. The atlas is one texture fetched after boot (the lite one on phones
// and software GL); until it lands, the old block recipes stand in.

import * as THREE from "three";

import { FX_OUT_GLSL, FX_UNIFORMS } from "./streaks.js";
import { FRAME_U, FRAME_V, FX_MODE, FX_SHEETS, FX_SHEET_NAMES, frameUV } from "./fx_sheets.js";
import { SoAPool } from "./soapool.js";

// Literal URLs so Vite content-hashes them into assets/models/ (build-models.mjs
// packs them there from art/fx), where sw.js keeps them in the model cache: a
// returning pilot's bursts are on the disk, not on the wire. Lossless WebP —
// the same texels as the PNG it replaced, a third fewer bytes.
const ATLAS_URL = new URL("../models/fx_atlas.webp", import.meta.url);
const ATLAS_LITE_URL = new URL("../models/fx_atlas_lite.webp", import.meta.url);
const RECTS_URL = new URL("../models/fx_atlas.json", import.meta.url);

// A card thins away over this span of distances from the eye, in card sizes:
// gone at the first, whole at the second.
const NEAR_FADE = [0.3, 0.75];
// How the sky lights straight gas: its fill (hemisphere plus ambient) and its
// key light, the smoke ropes' own balance (streaks.js).
const GAS_LIGHT = [0.62, 0.3];
// How far a card is slid toward the eye by default, in its own sizes.
const PUSH = 0.35;
// The share of tier 2's emitter lift (gfx2.js HDR_EMIT, 3.5) a burst's fire
// takes: ~1.9x, so a fireball crosses the bloom threshold and still reads
// orange through the film curve (the full lift bleached it to cream).
const HDR_SHARE = 0.35;

/** The sheets by index, with each frame's texture coordinate precomputed. */
export const SHEET_LIST = FX_SHEET_NAMES.map((name, id) => {
  const s = FX_SHEETS[name];
  const uv = new Float32Array(s.frames * 2);
  for (let k = 0; k < s.frames; k++) uv.set(frameUV(name, k), k * 2);
  return { ...s, name, id, mode: FX_MODE[s.blend], uv };
});
export const SHEET = Object.fromEntries(SHEET_LIST.map((s) => [s.name, s.id]));

/**
 * The atlas as every card and flash shares it: the texture (once it lands),
 * each frame's trimmed rectangle, and whether both are here yet.
 */
export const FX = {
  uAtlas: { value: null },
  uReady: { value: 0 },
  rects: SHEET_LIST.map((s) => {
    // Until the real rectangles land: the whole frame.
    const r = new Float32Array(s.frames * 4);
    for (let k = 0; k < s.frames; k++) r.set([0, 1, 0, 1], k * 4);
    return r;
  }),
  ready: false,
  lite: false,
  loading: null,
};

/**
 * Take each sheet's trimmed frame rectangles ({ sheet: [x0, x1, y0, y1, …] },
 * fx_atlas.json's `rects`, or packFlipbooks' in the tests).
 */
export function setRects(rects) {
  for (const s of SHEET_LIST) {
    const r = rects?.[s.name];
    if (r && r.length === s.frames * 4) FX.rects[s.id].set(r);
  }
}

/**
 * Mark the atlas here (the unit tests, which draw nothing, say so without a
 * texture). `texture` may be null.
 */
export function setAtlas(texture, rects = null) {
  if (rects) setRects(rects);
  FX.uAtlas.value = texture;
  FX.ready = true;
  FX.uReady.value = texture ? 1 : 0;
}

/**
 * Fetch the atlas and its rectangles — the half-size one when `lite` (phones,
 * software GL). Resolves true once the cards can draw; a failure leaves the
 * block particles standing in and says so once.
 */
export function loadFlipbooks({ lite = false } = {}) {
  if (FX.loading) return FX.loading;
  if (typeof document === "undefined") return (FX.loading = Promise.resolve(false));
  FX.lite = lite;
  const rects = fetch(RECTS_URL.href).then((r) => {
    if (!r.ok) throw new Error(`fx_atlas.json: HTTP ${r.status}`);
    return r.json();
  });
  const tex = new Promise((resolve, reject) => {
    new THREE.TextureLoader().load((lite ? ATLAS_LITE_URL : ATLAS_URL).href, resolve, undefined, reject);
  });
  FX.loading = Promise.all([rects, tex])
    .then(([json, t]) => {
      // Packed premultiplied already (fx-atlas.mjs); the shader decodes the
      // light itself, as the clouds do.
      t.colorSpace = THREE.NoColorSpace;
      t.premultiplyAlpha = false;
      t.generateMipmaps = true;
      t.minFilter = THREE.LinearMipmapLinearFilter;
      t.magFilter = THREE.LinearFilter;
      t.needsUpdate = true;
      setAtlas(t, json.rects);
      return true;
    })
    .catch((err) => {
      console.warn("effect flipbooks unavailable — the bursts stay blocks:", err?.message ?? err);
      return false;
    });
  return FX.loading;
}

// --- the cards ---------------------------------------------------------------

const VERT = /* glsl */ `
attribute vec2 corner;  // the card's corner, 0..1 across its rectangle
attribute vec4 iPos;    // xyz: the emitter (world); w: the frame's size (m)
attribute vec4 iRect;   // the card's rectangle in its frame: x0, x1, y0, y1 (y up)
attribute vec4 iUv;     // frame A's and frame B's bottom-left texture coordinates
attribute vec4 iLook;   // x: roll; y: opacity; z: blend A -> B; w: mirror (+1 | -1)
attribute vec4 iMode;   // xy: the emitter's place in its frame; z: blend mode; w: emission
attribute vec4 iTint;   // rgb: tint; w: slide toward the eye, in sizes
attribute vec4 iDir;    // xyz: the card's axis (0: upright); w: stretch — along the axis a
                        // card lies along, across a standing one (negative: it stands on
                        // the axis, not the world's up)
uniform vec3 uCamRight;
uniform vec2 uNearFade;
uniform vec2 uFrame;    // one frame's size in texture coordinates
varying vec2 vUvA;
varying vec2 vUvB;
varying float vMix;
varying float vAlpha;
varying float vMode;
varying float vEmit;
varying vec3 vTint;
#include <fog_pars_vertex>

void main() {
  float size = iPos.w;
  vec3 at = iPos.xyz;
  vec3 toEye = cameraPosition - at;
  float dist = length(toEye);
  float alpha = iLook.y * smoothstep(uNearFade.x * size, uNearFade.y * size, dist);
  vec3 n = toEye / max(dist, 1e-3);
  vec3 right;
  float stretch = abs(iDir.w);
  float axis = length(iDir.xyz);
  if (axis > 1e-4 && iDir.w > 0.0) {
    // Along an axis (a fireball thrown down a bore): the axis as the eye
    // sees it. Seen straight down it there's no length left to show, and it
    // rounds off into a ball (a lance swaps to its star before that; see
    // Sprites._write).
    vec3 d = iDir.xyz / axis;
    vec3 r = d - n * dot(d, n);
    float rl = length(r);
    right = rl > 1e-3 ? r / rl : uCamRight;
    stretch = mix(1.0, stretch, smoothstep(0.08, 0.4, rl));
  } else {
    // Standing: upright on the world's up, or on an axis of its own (a
    // negative stretch) — a flame streaming back off a burning airframe
    // stands on the wind it burns in. The card's right is square to that
    // axis as the eye sees it; looking straight along the axis there's no
    // square to find, so hand over to the camera's right.
    vec3 a = axis > 1e-4 ? iDir.xyz / axis : vec3(0.0, 1.0, 0.0);
    vec3 level = cross(a, n);
    float h = length(level);
    right = normalize(mix(uCamRight, level / max(h, 1e-4), smoothstep(0.08, 0.3, h)));
  }
  if (alpha < 0.003) {
    // Gone (or the eye is in it): park the card outside the clip volume, so a
    // quad that would cover the screen costs no fragments.
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  vec3 up = normalize(cross(n, right));
  float c = cos(iLook.x), s = sin(iLook.x);
  vec3 rr = right * c + up * s;
  vec3 uu = up * c - right * s;
  float fx = mix(iRect.x, iRect.y, corner.x);
  float fy = mix(iRect.z, iRect.w, corner.y);
  // Slide toward the eye and scale back up: the same pixels, drawn in front
  // of the ground the burst stands on.
  float push = min(iTint.w * size, dist * 0.5);
  float k = (dist - push) / max(dist, 1e-3);
  vec3 wp = at + n * push + (rr * ((fx - iMode.x) * size * stretch) + uu * ((fy - iMode.y) * size)) * k;
  // A mirrored card flips its picture inside the frame (the rectangle and the
  // emitter's place arrive already mirrored).
  vec2 f = vec2(iLook.w < 0.0 ? 1.0 - fx : fx, fy) * uFrame;
  vUvA = iUv.xy + f;
  vUvB = iUv.zw + f;
  vMix = iLook.z;
  vAlpha = alpha;
  vMode = iMode.z;
  vEmit = iMode.w;
  vTint = iTint.rgb;
  vec4 mvPosition = viewMatrix * vec4(wp, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uAtlas;
uniform vec3 uAmb;
uniform vec3 uSun;
uniform vec2 uGasLight;
uniform vec3 uGain;     // the material's colour: tier 2 lifts the light into HDR
uniform float uGainK;   // how much of that lift a burst's fire takes
varying vec2 vUvA;
varying vec2 vUvB;
varying float vMix;
varying float vAlpha;
varying float vMode;
varying float vEmit;
varying vec3 vTint;
#include <fog_pars_fragment>
FX_OUT

void main() {
  #ifdef ONE_TAP
    // Phones and software GL: the nearer frame alone, one fetch a fragment.
    vec4 t = texture2D(uAtlas, vMix < 0.5 ? vUvA : vUvB);
  #else
    vec4 t = mix(texture2D(uAtlas, vUvA), texture2D(uAtlas, vUvB), vMix);
  #endif
  // The atlas holds sRGB-encoded, premultiplied texels; squared back to
  // linear (near enough for light, and cheaper than a pow per pixel).
  vec3 sky = uAmb * uGasLight.x + uSun * uGasLight.y;
  // A share of tier 2's lift: enough that the fire blooms, not so much the
  // film curve bleaches a fireball's orange to cream.
  vec3 gain = 1.0 + (uGain - 1.0) * uGainK;
  vec3 gas;   // straight colour of what occludes, lit
  vec3 light; // light added, premultiplied
  float cover;
  if (vMode < 0.5) {
    // Gas lit neutral grey: tinted (white spray, brown dust, oily smoke) and
    // lit by the sky.
    vec3 g = t.rgb / max(t.a, 0.004);
    gas = g * g * vTint * sky;
    light = vec3(0.0);
    cover = t.a;
  } else if (vMode < 1.5) {
    // Light on black. A little coverage keeps its colour against a bright
    // sky, where pure addition washes to white (the tracers' rule).
    vec3 l = t.rgb * t.rgb;
    gas = vec3(0.0);
    light = l * vTint * vEmit * gain;
    cover = max(l.r, max(l.g, l.b)) * 0.45;
  } else {
    // Fire and smoke in one sheet: what burns adds light, what doesn't is
    // smoke, and the sky lights it.
    vec3 l = t.rgb * t.rgb;
    vec3 s = l / max(t.a, 0.004);
    float heat = smoothstep(0.035, 0.16, max(s.r, max(s.g, s.b)));
    gas = s * (1.0 - heat) * vTint * sky;
    light = l * heat * vEmit * gain;
    cover = t.a;
  }
  cover *= vAlpha;
  light *= vAlpha;
  if (cover < 0.002 && max(light.r, max(light.g, light.b)) < 0.002) discard;
  #ifdef USE_FOG
    #ifdef FOG_EXP2
      float fogF = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
    #else
      float fogF = smoothstep(fogNear, fogFar, vFogDepth);
    #endif
    // Haze stands in front of the gas and swallows the light.
    gas = mix(gas, fogColor, fogF);
    light *= 1.0 - fogF;
  #endif
  gl_FragColor = vec4(fxOut(gas) * cover + fxLight(light), cover);
}
`;

// The pool's fields: where it is and how it moves, how it ages and plays,
// how it looks.
const F32 = [
  "px", "py", "pz", "vx", "vy", "vz",
  "age", "life", "size", "grow", "drag", "rise", "wind",
  "f0", "fEnd", "fps", "roll", "spin",
  "alpha", "fadeIn", "fadeOut", "emit", "r", "g", "b", "push",
  "dx", "dy", "dz", "stretch", "bias",
];
// flags: bit 0 mirrored, bit 1 looping.
const U8 = ["sheet", "flags"];

/**
 * Every soft particle in the world: the ones that live a while (spawn) and
 * the ones redrawn each frame by their owner (push — a smoke screen's bank).
 * update() ages the first, draws both, back to front, and forgets the second.
 * `lite` (phones, software GL) samples one frame a card instead of blending
 * two: half the texture fetches, on the clients that pay for every one.
 */
export class Sprites {
  constructor(scene, { capacity = 768, immediates = 640, lite = false } = {}) {
    this.capacity = capacity;
    this.pool = new SoAPool(capacity, { f32: F32, u8: U8 });
    this.now = new SoAPool(immediates, { f32: F32, u8: U8 });
    this.time = 0;
    this.count = 0; // cards drawn last update (tests, probes)
    // (eye, x, y, z) -> 0..1: how much of a card there shows through what
    // stands between it and the eye (a smoke screen's bank); null: all of it.
    this.veil = null;
    const n = capacity + immediates;
    this.drawCap = n;
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute("corner", new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    // `position` only so three knows the vertex count; the shader places them.
    g.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array(12), 3));
    // three sorts see-through draws by the middle of this sphere; update()
    // moves it to the nearest card.
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    const attr = (name) => {
      const a = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4);
      a.setUsage(THREE.DynamicDrawUsage);
      g.setAttribute(name, a);
      return a;
    };
    this.attrs = ["iPos", "iRect", "iUv", "iLook", "iMode", "iTint", "iDir"].map(attr);
    [this.iPos, this.iRect, this.iUv, this.iLook, this.iMode, this.iTint, this.iDir] = this.attrs;
    g.instanceCount = 0;
    this.geometry = g;
    // The material's colour multiplies the light the cards add (not the gas
    // they're lit by): gfx2.js lifts it into HDR so the fire blooms.
    this.color = new THREE.Color(1, 1, 1);
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
        uAtlas: FX.uAtlas,
        uAmb: FX_UNIFORMS.uAmb,
        uSun: FX_UNIFORMS.uSun,
        uCamRight: FX_UNIFORMS.uCamRight,
        uNearFade: { value: new THREE.Vector2(...NEAR_FADE) },
        uFrame: { value: new THREE.Vector2(FRAME_U, FRAME_V) },
        uGasLight: { value: new THREE.Vector2(...GAS_LIGHT) },
        uGain: { value: this.color },
        uGainK: { value: HDR_SHARE },
      },
      defines: lite ? { ONE_TAP: "" } : {},
      vertexShader: VERT,
      fragmentShader: FRAG.replace("FX_OUT", FX_OUT_GLSL),
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide, // rolled and mirrored cards keep whatever winding that makes
      fog: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor,
      blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
    });
    this.material.color = this.color; // gfx2.js's emitter hook multiplies .color
    this.mesh = new THREE.Mesh(g, this.material);
    this.mesh.frustumCulled = false; // cards span the map; the quad sits at the origin
    this.mesh.visible = false;
    scene.add(this.mesh);
    // Draw order scratch: which pool each card is from, its slot, its depth.
    this._src = new Uint8Array(n);
    this._slot = new Uint16Array(n);
    this._key = new Float32Array(n);
    this._order = new Uint32Array(n);
  }

  /** Can the cards draw yet (the atlas has landed)? */
  get ready() {
    return FX.ready;
  }

  /**
   * A card that lives a while: `sheet` (a FX_SHEETS name) at (x, y, z), the
   * emitter's place in its frame. Options (all optional):
   *   size   — the frame's width in metres (default the sheet's own)
   *   grow   — how much wider it ends than it starts (0.5: half again)
   *   life   — seconds (default: the sheet's frames at its own rate)
   *   speed  — playback rate against the sheet's fps; frame / end: the first
   *            and last frame played (a card holds its last frame to the end
   *            of its life; a looping sheet wraps instead)
   *   vx, vy, vz, drag, rise (m/s², + up), wind (share of the wind it rides)
   *   alpha, fadeIn, fadeOut (s)
   *   tint   — [r, g, b]: the gas's colour, or the light's
   *   emit   — how bright its light burns (1: as baked)
   *   roll, spin, mirror
   *   push   — how far it slides toward the eye, in sizes
   *   dir    — [x, y, z]: lie along this axis instead of standing upright;
   *            stretch: its width along it (or across, standing upright)
   *   stand  — [x, y, z]: stand on this axis instead of the world's up — a
   *            flame streaming back off a moving airframe; stretch: its
   *            width across it
   *   bias   — metres added to its distance when the cards are sorted: a
   *            burst's own smoke sorts behind its fire (+), the flash in
   *            front of everything it lit (-)
   *   delay  — seconds before it appears (a second lobe a beat behind the
   *            first); it waits where it was spawned, unaged
   * Returns the pool slot (tests), or -1 for an unknown sheet.
   */
  spawn(sheet, x, y, z, o = {}) {
    return this._put(this.pool, sheet, x, y, z, o);
  }

  /** A card for this frame only (its owner draws it again next frame, or not). */
  push(sheet, x, y, z, o = {}) {
    return this._put(this.now, sheet, x, y, z, o);
  }

  _put(pool, sheet, x, y, z, o) {
    const s = SHEET_LIST[SHEET[sheet]];
    if (!s) return -1;
    const i = pool.push();
    const f = pool.f;
    const fps = s.fps * (o.speed ?? 1);
    const f0 = o.frame ?? 0;
    const fEnd = Math.min(o.end ?? s.frames - 1, s.frames - 1);
    f.px[i] = x; f.py[i] = y; f.pz[i] = z;
    f.vx[i] = o.vx ?? 0; f.vy[i] = o.vy ?? 0; f.vz[i] = o.vz ?? 0;
    f.age[i] = -(o.delay ?? 0);
    f.life[i] = o.life ?? (fps > 0 ? (fEnd - f0 + 1) / fps : 0.1);
    f.size[i] = o.size ?? s.frameM;
    f.grow[i] = o.grow ?? 0;
    f.drag[i] = o.drag ?? 0;
    f.rise[i] = o.rise ?? 0;
    f.wind[i] = o.wind ?? 0;
    f.f0[i] = f0;
    f.fEnd[i] = fEnd;
    f.fps[i] = fps;
    f.roll[i] = o.roll ?? 0;
    f.spin[i] = o.spin ?? 0;
    f.alpha[i] = o.alpha ?? 1;
    f.fadeIn[i] = o.fadeIn ?? 0;
    f.fadeOut[i] = o.fadeOut ?? 0;
    f.emit[i] = o.emit ?? 1;
    const t = o.tint;
    f.r[i] = t ? t[0] : 1; f.g[i] = t ? t[1] : 1; f.b[i] = t ? t[2] : 1;
    f.push[i] = o.push ?? PUSH;
    // The card's axis: one it lies along (dir), or one it stands on (stand),
    // told apart by the stretch's sign (the vertex shader reads it the same).
    const d = o.dir ?? o.stand;
    f.dx[i] = d ? d[0] : 0; f.dy[i] = d ? d[1] : 0; f.dz[i] = d ? d[2] : 0;
    f.stretch[i] = !o.dir && o.stand ? -(o.stretch ?? 1) : o.stretch ?? 1;
    f.bias[i] = o.bias ?? 0;
    pool.b.sheet[i] = s.id;
    pool.b.flags[i] = (o.mirror ? 1 : 0) | (s.loop || o.loop ? 2 : 0);
    return i;
  }

  /** The frame a card shows at its age (fractional: the blend toward the next). */
  static frameAt(pool, i) {
    const f = pool.f;
    const s = SHEET_LIST[pool.b.sheet[i]];
    const t = f.f0[i] + Math.max(0, f.age[i]) * f.fps[i];
    if (pool.b.flags[i] & 2) return t % s.frames;
    return Math.min(t, f.fEnd[i]);
  }

  /**
   * Age the cards by dt (the effects clock: slow-mo slows them), carry them on
   * their drift and the wind (windX, windZ m/s), and draw every live one,
   * farthest first, as seen from `eye`.
   */
  update(dt, { eye = null, windX = 0, windZ = 0 } = {}) {
    this.time += dt;
    const p = this.pool;
    const { px, py, pz, vx, vy, vz, age, life, drag, rise, wind, roll, spin } = p.f;
    let live = 0;
    for (let k = 0; k < p.count; k++) {
      const a = age[k] + dt;
      if (a >= life[k]) continue;
      if (live !== k) p.copySlot(k, live);
      age[live] = a;
      if (a <= 0) {
        live++; // still waiting on its delay: where it was spawned, unaged
        continue;
      }
      const damp = Math.max(0, 1 - drag[live] * dt);
      const nvx = vx[live] * damp;
      const nvy = (vy[live] + rise[live] * dt) * damp;
      const nvz = vz[live] * damp;
      vx[live] = nvx; vy[live] = nvy; vz[live] = nvz;
      const w = wind[live];
      px[live] += (nvx + windX * w) * dt;
      py[live] += nvy * dt;
      pz[live] += (nvz + windZ * w) * dt;
      roll[live] += spin[live] * dt;
      live++;
    }
    p.count = live;
    this._draw(eye);
    this.now.count = 0; // this frame's pushes are drawn; the owners push again
  }

  _draw(eye) {
    this._eye = eye;
    const src = this._src, slot = this._slot, key = this._key, order = this._order;
    const ex = eye?.x ?? 0, ey = eye?.y ?? 0, ez = eye?.z ?? 0;
    let n = 0;
    let best = Infinity, bx = 0, by = 0, bz = 0;
    for (let which = 0; which < 2; which++) {
      const pool = which ? this.now : this.pool;
      const { px, py, pz, size, bias } = pool.f;
      const sheetA = pool.b.sheet;
      for (let i = 0; i < pool.count && n < this.drawCap; i++) {
        // Sorted by the card's middle, not its emitter: a splash column's
        // emitter is its foot, a hundred metres below the spray.
        const s = SHEET_LIST[sheetA[i]];
        const my = py[i] + (0.5 - s.anchor[1]) * size[i];
        const dx = px[i] - ex, dy = my - ey, dz = pz[i] - ez;
        const d2 = dx * dx + dy * dy + dz * dz;
        src[n] = which;
        slot[n] = i;
        key[n] = Math.sqrt(d2) + bias[i];
        order[n] = n;
        n++;
        if (d2 < best) { best = d2; bx = px[i]; by = my; bz = pz[i]; }
      }
    }
    // Back to front (only with an eye to sort from; the tests draw in spawn order).
    const ord = order.subarray(0, n);
    if (eye) ord.sort((a, b) => key[b] - key[a]);
    let w = 0;
    for (let j = 0; j < n; j++) {
      const e = ord[j];
      if (this._write(w, src[e] ? this.now : this.pool, slot[e])) w++;
    }
    for (const a of this.attrs) {
      a.clearUpdateRanges();
      if (w > 0) a.addUpdateRange(0, w * 4);
      a.needsUpdate = w > 0;
    }
    this.geometry.instanceCount = w;
    this.count = w;
    this.mesh.visible = w > 0 && FX.ready;
    if (best < Infinity) this.geometry.boundingSphere.center.set(bx, by, bz);
  }

  // Write card `i` of `pool` into draw slot `w`; false if it shows nothing.
  _write(w, pool, i) {
    const f = pool.f;
    const age = f.age[i], life = f.life[i];
    if (age < 0) return false; // not yet: a delayed card waits unseen
    let alpha = f.alpha[i];
    if (f.fadeIn[i] > 0) alpha *= Math.min(1, age / f.fadeIn[i]);
    if (f.fadeOut[i] > 0) alpha *= Math.min(1, (life - age) / f.fadeOut[i]);
    if (alpha < 0.003) return false;
    const s = SHEET_LIST[pool.b.sheet[i]];
    // Sunk into any smoke screen between it and the eye (effects.js Smoke),
    // measured to the card's middle.
    if (this.veil && this._eye) {
      alpha *= this.veil(this._eye, f.px[i], f.py[i] + (0.5 - s.anchor[1]) * f.size[i], f.pz[i]);
      if (alpha < 0.003) return false;
    }
    const flags = pool.b.flags[i];
    const loop = flags & 2;
    const fr = Sprites.frameAt(pool, i);
    let A = Math.floor(fr);
    // A held frame (a flash, a screen's billow) blends with nothing.
    let B = f.fps[i] === 0 ? A : loop ? (A + 1) % s.frames : Math.min(A + 1, f.fEnd[i]);
    const mix = B === A ? 0 : fr - A;
    let dx = f.dx[i], dy = f.dy[i], dz = f.dz[i], stretch = f.stretch[i];
    if (s.anchorEndOn && A < s.cols && stretch > 0 && (dx || dy || dz) && this._eye) {
      // A flash lance seen straight down its bore — from the wrong end of the
      // guns — is its star instead.
      const ex = this._eye.x - f.px[i], ey = this._eye.y - f.py[i], ez = this._eye.z - f.pz[i];
      const c = (ex * dx + ey * dy + ez * dz) / (Math.hypot(ex, ey, ez) * Math.hypot(dx, dy, dz) || 1);
      if (Math.abs(c) > 0.9) {
        A += s.cols;
        B = A;
        dx = dy = dz = 0;
        stretch = 1;
      }
    }
    const rects = FX.rects[s.id];
    let x0 = Math.min(rects[A * 4], rects[B * 4]);
    let x1 = Math.max(rects[A * 4 + 1], rects[B * 4 + 1]);
    const y0 = Math.min(rects[A * 4 + 2], rects[B * 4 + 2]);
    const y1 = Math.max(rects[A * 4 + 3], rects[B * 4 + 3]);
    if (x1 <= x0 || y1 <= y0) return false; // both frames empty
    const an = s.anchorEndOn && A >= s.cols ? s.anchorEndOn : s.anchor;
    let ax = an[0];
    const mirror = flags & 1;
    if (mirror) {
      const t = x0;
      x0 = 1 - x1;
      x1 = 1 - t;
      ax = 1 - ax;
    }
    // Wider as it ages: fast at first, easing out (a burst blooms, then hangs).
    const t = life > 0 ? age / life : 1;
    const size = f.size[i] * (1 + f.grow[i] * (1 - (1 - t) * (1 - t)));
    const k = w * 4;
    const P = this.iPos.array, R = this.iRect.array, U = this.iUv.array, L = this.iLook.array;
    const M = this.iMode.array, T = this.iTint.array, D = this.iDir.array;
    P[k] = f.px[i]; P[k + 1] = f.py[i]; P[k + 2] = f.pz[i]; P[k + 3] = size;
    R[k] = x0; R[k + 1] = x1; R[k + 2] = y0; R[k + 3] = y1;
    U[k] = s.uv[A * 2]; U[k + 1] = s.uv[A * 2 + 1]; U[k + 2] = s.uv[B * 2]; U[k + 3] = s.uv[B * 2 + 1];
    L[k] = f.roll[i]; L[k + 1] = alpha; L[k + 2] = mix; L[k + 3] = mirror ? -1 : 1;
    M[k] = ax; M[k + 1] = an[1]; M[k + 2] = s.mode; M[k + 3] = f.emit[i];
    T[k] = f.r[i]; T[k + 1] = f.g[i]; T[k + 2] = f.b[i]; T[k + 3] = f.push[i];
    D[k] = dx; D[k + 1] = dy; D[k + 2] = dz; D[k + 3] = stretch;
    return true;
  }
}

// --- muzzle flashes --------------------------------------------------------------

// The flash cards: two crossed cards along the bore wearing a side-on flash,
// and one across it wearing an end-on star. Each fades as the eye swings off
// its face, so from abeam you see the flame lance out of the barrel and from
// dead ahead — the wrong end of a fighter's guns — the star.
const FLASH_VERT = /* glsl */ `
attribute float face;   // 0: along the bore, 1: across it
attribute vec2 local;   // where in its frame the corner is (0..1)
varying vec2 vUv;
varying vec2 vLocal;
varying float vFade;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vec3 nrm = normalize(normalMatrix * normal);
  float facing = abs(dot(nrm, normalize(-mv.xyz)));
  vFade = face < 0.5 ? smoothstep(0.12, 0.55, facing) : smoothstep(0.3, 0.85, facing);
  vUv = uv;
  vLocal = local;
  gl_Position = projectionMatrix * mv;
}
`;

const FLASH_FRAG = /* glsl */ `
uniform sampler2D uAtlas;
uniform float uReady;
uniform vec3 uGain;
varying vec2 vUv;
varying vec2 vLocal;
varying float vFade;
FX_OUT
void main() {
  vec3 l;
  if (uReady > 0.5) {
    vec4 t = texture2D(uAtlas, vUv);
    l = t.rgb * t.rgb;
  } else {
    // Until the atlas lands: a soft hot glow where the flash will be.
    float d = length((vLocal - 0.5) * vec2(1.6, 3.2));
    l = vec3(1.0, 0.82, 0.45) * pow(max(1.0 - d, 0.0), 2.0);
  }
  l *= vFade;
  if (max(l.r, max(l.g, l.b)) < 0.003) discard;
  gl_FragColor = vec4(fxLight(l * uGain), max(l.r, max(l.g, l.b)) * 0.4);
}
`;

// The side-on flash's gas sits in the middle band of its frame, the star in
// the middle of its own; the cards are trimmed to that (flipbooks.test.mjs
// holds every flash inside these).
export const FLASH_SIDE_RECT = [0.03, 0.8, 0.26, 0.74];
export const FLASH_END_RECT = [0.02, 0.98, 0.02, 0.98];

/** The flash cards' shared material (gfx2.js lifts its colour into HDR). */
export const flashMaterial = new THREE.ShaderMaterial({
  uniforms: { uAtlas: FX.uAtlas, uReady: FX.uReady, uGain: { value: new THREE.Color(1, 1, 1) } },
  vertexShader: FLASH_VERT,
  fragmentShader: FLASH_FRAG.replace("FX_OUT", FX_OUT_GLSL),
  transparent: true,
  depthWrite: false,
  side: THREE.DoubleSide,
  blending: THREE.CustomBlending,
  blendSrc: THREE.OneFactor,
  blendDst: THREE.OneMinusSrcAlphaFactor,
  blendSrcAlpha: THREE.OneFactor,
  blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
});
flashMaterial.color = flashMaterial.uniforms.uGain.value;

// One geometry per variant: side-on frame k and end-on star k, a 1 m frame
// (the mesh's scale sizes it), the muzzle at the origin, the bore along +X.
function flashGeometry(k) {
  const s = FX_SHEETS.muzzle;
  const side = frameUV("muzzle", s.sideOn[k]);
  const end = frameUV("muzzle", s.endOn[k]);
  const pos = [], uv = [], local = [], nrm = [], face = [], idx = [];
  const quad = (corners, n, base, rect, isEnd) => {
    const at = pos.length / 3;
    for (const [fx, fy, p] of corners(rect)) {
      pos.push(...p);
      uv.push(base[0] + fx * FRAME_U, base[1] + fy * FRAME_V);
      local.push(fx, fy);
      nrm.push(...n);
      face.push(isEnd ? 1 : 0);
    }
    idx.push(at, at + 1, at + 2, at, at + 2, at + 3);
  };
  const [ax, ay] = s.anchor;
  const [ex, ey] = s.anchorEndOn;
  const sideCorners = (plane) => ([x0, x1, y0, y1]) =>
    [[x0, y0], [x1, y0], [x1, y1], [x0, y1]].map(([fx, fy]) => {
      const a = fx - ax, b = fy - ay;
      return [fx, fy, plane === "xy" ? [a, b, 0] : [a, 0, b]];
    });
  quad(sideCorners("xy"), [0, 0, 1], side, FLASH_SIDE_RECT, false);
  quad(sideCorners("xz"), [0, 1, 0], side, FLASH_SIDE_RECT, false);
  // The star stands a hair ahead of the muzzle, across the bore, a little
  // smaller than the lance it's the end of.
  const endCorners = ([x0, x1, y0, y1]) =>
    [[x0, y0], [x1, y0], [x1, y1], [x0, y1]].map(([fx, fy]) => [fx, fy, [0.02, (fy - ey) * 0.8, (fx - ex) * 0.8]]);
  quad(endCorners, [1, 0, 0], end, FLASH_END_RECT, true);
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute("local", new THREE.Float32BufferAttribute(local, 2));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute("face", new THREE.Float32BufferAttribute(face, 1));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}
const FLASH_GEOS = FX_SHEETS.muzzle.sideOn.map((_, k) => flashGeometry(k));

// Every frame a flash is drawn it takes a fresh shape for the next one — a
// new variant, a new roll about the bore, a new reach — so a stream of fire
// flickers instead of stamping one picture: the burn is never the same twice.
const _jitter = new THREE.Matrix4();
const _scale = new THREE.Vector3();
function flicker() {
  this.geometry = FLASH_GEOS[(Math.random() * FLASH_GEOS.length) | 0];
  const reach = 0.8 + Math.random() * 0.45;
  const girth = 0.85 + Math.random() * 0.3;
  _jitter.makeRotationX(Math.random() * Math.PI * 2).scale(_scale.set(reach, girth, girth));
  this.matrixWorld.multiply(_jitter);
  // The jitter rides on the world matrix itself, so the next walk must
  // rebuild it from the gun: WorldScene only rebuilds what it's told moved,
  // and a flash held on a still gun would otherwise spin and swell on its
  // own last frame's jitter.
  this.matrixWorldNeedsUpdate = true;
}

/**
 * A muzzle flash for a gun whose bore runs along its parent's +X: the mesh's
 * origin sits on the muzzle, and `size` is its frame's width in metres (the
 * lance reaches about 0.55 of it past the muzzle). Hidden until its owner
 * shows it, the way the flash boxes were.
 */
export function muzzleFlash(size = 2) {
  const m = new THREE.Mesh(FLASH_GEOS[0], flashMaterial);
  m.scale.setScalar(size);
  m.visible = false;
  m.frustumCulled = false; // a flash is a few metres; the sort point is its muzzle
  m.renderOrder = 1; // over the smoke rolling off the same gun
  m.onBeforeRender = flicker;
  return m;
}
